import { RefObject, useCallback, useEffect } from 'react';
import { useRightDrawer } from '@/contexts/DrawerContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { useNotificationStore } from '@/stores/notificationStore';
import { useFixedChatStore } from '@/stores/fixedChatStore';

interface ResizableChart {
  resize: (opts?: { width?: number | 'auto'; height?: number | 'auto' }) => void;
  /** ECharts 实例的容器元素 */
  getDom?: () => HTMLElement;
}

/**
 * 缩小窗口时，canvas 的固定像素宽度会撑住 flex/grid 祖先（min-width: auto），
 * 容器缩不回去导致 resize() 量到旧宽度。先把图表容器宽度临时置 0 释放支撑，
 * 量出父容器真实宽度后再按该宽度 resize。
 */
function resizeChart(chart: ResizableChart) {
  const dom = chart.getDom?.();
  const parent = dom?.parentElement;
  if (!dom || !parent) {
    chart.resize();
    return;
  }
  const prevWidth = dom.style.width;
  dom.style.width = '0px';
  const width = parent.clientWidth; // 读取即触发同步重排，得到收缩后的真实宽度
  dom.style.width = prevWidth;
  chart.resize(width > 0 ? { width } : undefined);
}

export default function useChartAutoResize(
  chartRef: RefObject<ResizableChart | null>,
  openInSidebar?: boolean,
) {
  const { isOpen: isRightDrawerOpen, drawerWidth } = useRightDrawer();
  const { chatId } = useFixedChatStore();
  const { isOpen: isNotificationOpen } = useNotificationStore();
  const { isCollapsed } = useSidebar();

  const scheduleResize = useCallback(() => {
    const resize = () => {
      if (chartRef.current) resizeChart(chartRef.current);
    };
    const frame = window.requestAnimationFrame(resize);
    const timer = window.setTimeout(resize, 300);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [chartRef]);

  useEffect(() => {
    return scheduleResize();
  }, [
    scheduleResize,
    isCollapsed,
    isRightDrawerOpen,
    isNotificationOpen,
    chatId,
    drawerWidth,
    openInSidebar,
  ]);

  useEffect(() => {
    const handleResize = () => {
      scheduleResize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [scheduleResize]);
}
